import React from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { SectionCard } from '../../../components/ui';
import { StatusBadge } from '../../../components/ui';

export default function RequestsFiltersPanel({
  mainTab,
  mainTabs,
  onMainTabChange,
  requestType,
  onRequestTypeChange,
  finalSubTabs,
  activeBreakdown,
  isArchiveView,
  search,
  onSearchChange,
  searchRef,
  fromDate,
  onFromDateChange,
  toDate,
  onToDateChange,
  onApply,
  onPreset,
  onReset,
  onRefresh,
  onExport,
  exportLabel = 'Export',
  exporting = false,
  loading,
  updating,
  canExport,
  showBulkActions,
  selectedCount,
  onBulkApprove,
  onBulkReject,
  showTypeCounts = false,
  showBreakdown = false,
}) {
  const theme = useTheme();

  // Quick date presets + a plain-language readout of the active range.
  const DATE_PRESETS = [
    { key: 'Today', label: 'Today', days: 1 },
    { key: '7d', label: '7 days', days: 7 },
    { key: '30d', label: '30 days', days: 30 },
    { key: '90d', label: 'Last 3 months', days: 90 },
    { key: '180d', label: '6 months', days: 180 },
    { key: 'year', label: 'This year', days: 365 },
  ];
  const fmtDay = (d) => {
    const x = new Date(d);
    return Number.isNaN(x.getTime())
      ? d
      : x.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const rangeDays = (() => {
    if (!fromDate || !toDate) return null;
    const f = new Date(fromDate);
    const t = new Date(toDate);
    if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return null;
    return Math.round((t - f) / 86400000) + 1;
  })();
  const activePreset = DATE_PRESETS.find((p) => p.days === rangeDays) || null;

  const renderMainTabLabel = (tab) => {
    const count = Number(tab?.count || 0);
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7 }}>
        <Box component="span">{tab.label}</Box>
        {count > 0 ? (
          <Box
            component="span"
            className="tab-count"
            sx={{
              minWidth: 20,
              height: 20,
              px: 0.6,
              borderRadius: '999px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 900,
              lineHeight: 1,
              color: theme.palette.warning.dark,
              border: '1px solid',
              borderColor: alpha(theme.palette.warning.main, 0.42),
              backgroundColor: alpha(theme.palette.warning.main, 0.14),
            }}
          >
            {count}
          </Box>
        ) : null}
      </Box>
    );
  };

  return (
    <SectionCard tone="dashboard" disablePadding contentSx={{ p: 0 }} sx={{ mb: 1.25, overflow: 'hidden', borderRadius: '8px' }}>
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.4, sm: 1.8 },
          background:
            `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.065)} 0%, transparent 100%)`,
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ lg: 'center' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', letterSpacing: '0.08em', textTransform: 'uppercase' }}
            >
              Queue controls
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.35 }}>
              Filter &amp; search requests
            </Typography>
          </Box>

          <Tabs
            value={mainTab}
            onChange={onMainTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{
              minHeight: 46,
              '& .MuiTabs-flexContainer': { gap: 0.6 },
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                minHeight: 40,
                px: 1.5,
                py: 0.6,
                borderRadius: '8px',
                position: 'relative',
                color: 'text.secondary',
                fontWeight: 700,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.76),
              },
              '& .MuiTab-root.attention-tab:not(.Mui-selected)': {
                color: theme.palette.warning.dark,
                borderColor: alpha(theme.palette.warning.main, 0.56),
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.08)}`,
              },
              '& .MuiTab-root.attention-tab:not(.Mui-selected)::after': {
                content: '""',
                position: 'absolute',
                top: 6,
                right: 6,
                width: 7,
                height: 7,
                borderRadius: '50%',
                backgroundColor: theme.palette.warning.main,
                boxShadow: `0 0 0 4px ${alpha(theme.palette.warning.main, 0.16)}`,
              },
              '& .Mui-selected': {
                color: `${theme.palette.primary.contrastText} !important`,
                backgroundColor: theme.palette.primary.main,
                borderColor: alpha(theme.palette.primary.main, 0.3),
                boxShadow: theme.shadows[2],
              },
              '& .Mui-selected .tab-count': {
                color: theme.palette.primary.contrastText,
                borderColor: alpha(theme.palette.common.white, 0.38),
                backgroundColor: alpha(theme.palette.common.white, 0.22),
              },
            }}
          >
            {mainTabs.map((t) => (
              <Tab
                key={t.key}
                value={t.key}
                label={renderMainTabLabel(t)}
                className={t.attention ? 'attention-tab' : undefined}
              />
            ))}
          </Tabs>
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack spacing={1.35}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.1fr 1.6fr 0.7fr 0.7fr auto' },
              gap: 1.1,
              alignItems: 'stretch',
            }}
          >
            <FormControl size="small" fullWidth>
              <InputLabel id="hr-request-type-label">Request Type</InputLabel>
              <Select
                labelId="hr-request-type-label"
                label="Request Type"
                value={requestType}
                onChange={onRequestTypeChange}
              >
                <MenuItem value="all">All types</MenuItem>
                {finalSubTabs.map((st) => {
                  const scopedCount = activeBreakdown[st.key] || 0;
                  const Icon = st.Icon;
                  return (
                    <MenuItem key={st.key} value={st.key}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          gap: 1.25,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          {Icon ? <Icon fontSize="small" /> : null}
                          <span>{st.label}</span>
                        </Stack>
                        {showTypeCounts && scopedCount > 0 ? (
                          <StatusBadge
                            status={isArchiveView ? 'approved' : 'pending'}
                            label={String(scopedCount)}
                            size="small"
                            icon={false}
                            variant="outlined"
                          />
                        ) : null}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <TextField
              inputRef={searchRef}
              size="small"
              label="Search queue"
              placeholder="Name, status, request id, or details"
              value={search}
              onChange={onSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ opacity: 0.72 }} />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />

            <TextField
              type="date"
              label="From"
              size="small"
              value={fromDate}
              onChange={onFromDateChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              type="date"
              label="To"
              size="small"
              value={toDate}
              onChange={onToDateChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <Button
              variant="contained"
              onClick={onApply}
              disabled={loading}
              sx={{ minWidth: 120, borderRadius: '8px', px: 2.4, fontWeight: 800 }}
            >
              {loading ? 'Loading...' : 'Apply'}
            </Button>
          </Box>

          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={1.2}
            justifyContent="space-between"
            alignItems={{ lg: 'center' }}
          >
            <Stack spacing={0.8} sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'inline-flex', alignItems: 'center', gap: 0.6, fontWeight: 600 }}
              >
                <CalendarMonthIcon sx={{ fontSize: 15 }} />
                {fromDate && toDate
                  ? `Showing ${fmtDay(fromDate)} – ${fmtDay(toDate)}${activePreset ? ` · ${activePreset.label.toLowerCase()}` : ' · custom range'}`
                  : 'Select a date range'}
              </Typography>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.7 }}>
                {DATE_PRESETS.map((p) => {
                  const active = activePreset?.key === p.key;
                  return (
                    <Chip
                      key={p.key}
                      label={p.label}
                      size="small"
                      color={active ? 'primary' : 'default'}
                      variant={active ? 'filled' : 'outlined'}
                      onClick={() => onPreset(p.key)}
                      sx={{ fontWeight: active ? 800 : 600, borderRadius: '8px' }}
                    />
                  );
                })}
                <Chip label="Reset" size="small" variant="outlined" onClick={onReset} sx={{ borderRadius: '8px' }} />
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="flex-end"
            >
              {showBulkActions ? (
                <ButtonGroup
                  size="small"
                  variant="outlined"
                  sx={{
                    '& .MuiButton-root': {
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 800,
                    },
                  }}
                >
                  <Button
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    disabled={updating}
                    onClick={onBulkApprove}
                  >
                    Approve ({selectedCount})
                  </Button>
                  <Button
                    color="error"
                    startIcon={<CancelIcon />}
                    disabled={updating}
                    onClick={onBulkReject}
                  >
                    Reject ({selectedCount})
                  </Button>
                </ButtonGroup>
              ) : null}

              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={onRefresh}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={onExport}
                  disabled={!canExport || loading || exporting}
                >
                  {exporting ? 'Exporting...' : exportLabel}
                </Button>
              </Stack>
            </Stack>
          </Stack>

          {showBreakdown && !!(activeBreakdown.total || 0) ? (
            <Box
              sx={{
                borderTop: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.08),
                pt: 1.25,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.65, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                {isArchiveView ? 'Archive by type' : 'Pending by type'} · independent of the filter above
              </Typography>
              <Stack direction="row" spacing={0.9} sx={{ flexWrap: 'wrap' }}>
                {finalSubTabs.map((st) => {
                  const count = activeBreakdown[st.key] || 0;
                  if (!count) return null;
                  // Highlight the chip that corresponds to the active filter
                  // so users can tell at a glance which slice the table shows.
                  const isActiveType = requestType !== 'all' && requestType === st.key;
                  return (
                    <Chip
                      key={st.key}
                      label={`${st.label} · ${count}`}
                      size="small"
                      sx={{
                        borderRadius: '8px',
                        backgroundColor: alpha(
                          isArchiveView ? theme.palette.info.main : theme.palette.warning.main,
                          isActiveType ? 0.22 : 0.08
                        ),
                        border: '1px solid',
                        borderColor: alpha(
                          isArchiveView ? theme.palette.info.main : theme.palette.warning.main,
                          isActiveType ? 0.6 : 0.2
                        ),
                        fontWeight: isActiveType ? 800 : 600,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          ) : showBreakdown ? (
            <Typography variant="caption" color="text.secondary" sx={{ pt: 0.2 }}>
              No type breakdown for the current queue and filters.
            </Typography>
          ) : null}
        </Stack>
      </Box>
    </SectionCard>
  );
}
